import { changeSortOrderAPI } from "../api/api";
import { logQuoteAPI } from "../api/endpoints/ApiLog";
import SortBy, { SortDirection } from "../enums/SortBy";
import IQuoteCardResult from "../interfaces/results/IQuoteCardResult";
import IQuoteCardResultSortBy from "../interfaces/results/IQuoteCardResultRating";
import useErrorLogger from "../../common/hooks/useErrorLogger";

const ErrorLogger = useErrorLogger();

interface IUseSorter {
    sortQuoteResult: (
        results: IQuoteCardResult[],
        sortBy: SortBy,
        groupId: string
    ) => IQuoteCardResult[];
}

function assignHighlightedQuoteCards(
    results: IQuoteCardResult[],
    key: keyof IQuoteCardResultSortBy,
    direction: SortDirection
): IQuoteCardResult[] {
    const orderByValues = results
        .map((card) => {
            if (card.result.errors.length <= 0) {
                return card.ratings[key].orderBy;
            }
        })
        .filter((result) => result);

    const bestChoiceValue =
        direction === "asc" ? Math.min(...orderByValues) : Math.max(...orderByValues);

    return (results = results.map((result) => {
        return {
            ...result,
            state: {
                ...result.state,
                isHighlighted:
                    result.ratings[key].orderBy === bestChoiceValue &&
                    !result.state.hasBlockingError
            }
        };
    }));
}

function sortByRating(
    results: IQuoteCardResult[],
    key: keyof IQuoteCardResultSortBy,
    direction: SortDirection
): IQuoteCardResult[] {
    // TODO: Streamline and merge sorts
    let sortedResults = [...results].sort((a, b) => {
        if (direction === "asc") return a.ratings[key].orderBy - b.ratings[key].orderBy;
        else return b.ratings[key].orderBy - a.ratings[key].orderBy;
    });

    sortedResults = sortedResults.sort(
        (a, b) =>
            (b.result.errors.length > 0 ? 0 : 1) - (a.result.errors.length > 0 ? 0 : 1)
    );

    return assignHighlightedQuoteCards(sortedResults, key, direction);
}

function getSortByValue(sortBy: SortBy): keyof IQuoteCardResultSortBy {
    switch (sortBy) {
        case SortBy.CustomerService:
            return "customerService";
        case SortBy.PolicyFeatures:
            return "provider";
        case SortBy.MostPopular:
            return "providerPopularity";
        case SortBy.FinancialStrength:
            return "financialStrength";
        case SortBy.LowestPrice:
        case SortBy.HighestPrice:
        default:
            return "premium";
    }
}

export function sortQuoteResult(
    cards: IQuoteCardResult[],
    sortBy: SortBy,
    groupId: string
): IQuoteCardResult[] {
    // Results are already sorted
    if (cards.length <= 1) return cards;

    const sortByString = getSortByValue(sortBy);

    let sortDirect: SortDirection = "desc";

    if (sortBy === SortBy.LowestPrice) sortDirect = "asc";

    const sortedResults = sortByRating(cards, sortByString, sortDirect);

    logQuoteAPI(sortBy, sortedResults, groupId).catch((err) =>
        ErrorLogger.Log(err, { source: "Use Sorter - Log Quote" })
    );

    Object.values(cards[0]?.result.settings).forEach((setting) => {
        changeSortOrderAPI(setting.caseId, sortBy, setting.productType).catch((err) => {
            ErrorLogger.Log(err, {
                source: "Use Sorter - Change Sort Order"
            });
        });
    });

    return sortedResults;
}

const useSorter = (): IUseSorter => {
    return {
        sortQuoteResult
    };
};

export default useSorter;
